/**
 * Comparator for sorting numbers in ascending order. Suitable for use as
 * the callback to `Array.prototype.sort`.
 *
 * Special-value ordering (low → high):
 * `-Infinity` < finite numbers < `+Infinity` < `NaN`
 *
 * NaN is placed after everything else so missing or unparseable values
 * naturally fall to the end of a sorted list.
 *
 * @param a - The first number to compare.
 * @param b - The second number to compare.
 * @returns `-1` if a sorts before b, `1` if a sorts after b, `0` if they are equal.
 *
 * @example
 * [NaN, 3, -Infinity, 1, Infinity].sort(compareNumbers)
 * // → [-Infinity, 1, 3, Infinity, NaN]
 */
export function compareNumbers(a: number, b: number): 0 | 1 | -1 {
  const aNaN = Number.isNaN(a);
  const bNaN = Number.isNaN(b);
  if (aNaN && bNaN) return 0;
  if (aNaN) return 1;
  if (bNaN) return -1;
  if (a === b) return 0;
  return a < b ? -1 : 1;
}
